import React from 'react';
import { 
  Box, Container, Typography, Button, Paper, Divider, 
  List, ListItem, ListItemIcon, ListItemText, Grid 
} from '@mui/material';
import { 
  Security, CheckCircle, Description, DataUsage, 
  SettingsSuggest, Share, Shield, Save, 
  ManageAccounts, Cookie, ChildCare, Public, 
  Update, Email, ShieldOutlined
} from '@mui/icons-material';

export default function PrivacyPolicy() {
  const primaryGradient = 'linear-gradient(135deg, #FF6A00 0%, #EE0979 100%)';
  const darkBg = '#070B14';
  const lightBg = '#f8fafc';

  // Helper component for styled lists
  const CustomListItem = ({ text }) => (
    <ListItem sx={{ py: 0.5, px: 0 }}>
      <ListItemIcon sx={{ minWidth: 32 }}>
        <CheckCircle sx={{ color: '#FF6A00', fontSize: 18 }} />
      </ListItemIcon>
      <ListItemText primary={text} primaryTypographyProps={{ color: '#475569', fontSize: '1rem', lineHeight: 1.5 }} />
    </ListItem>
  );

  // Helper component for section headers with icons
  const SectionHeader = ({ icon, title }) => (
    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, mt: 5 }}>
      <Box 
        sx={{ 
          width: 40, height: 40, borderRadius: '10px', 
          background: 'rgba(255,106,0,0.1)', color: '#FF6A00',
          display: 'flex', alignItems: 'center', justifyContent: 'center', mr: 2,
          flexShrink: 0
        }}
      >
        {icon}
      </Box>
      <Typography variant="h5" fontWeight="800" color="#1E293B">
        {title}
      </Typography>
    </Box>
  );

  return (
    <Box sx={{ bgcolor: lightBg, minHeight: '100vh', pb: 10 }}>
      
      {/* 🌟 PAGE HEADER 🌟 */}
      <Box sx={{ position: 'relative', pt: { xs: 12, md: 16 }, pb: { xs: 8, md: 10 }, bgcolor: darkBg, overflow: 'hidden' }}>
        <Box sx={{ position: 'absolute', top: 0, left: '50%', transform: 'translateX(-50%)', width: '60%', height: '2px', background: primaryGradient, boxShadow: '0 0 20px 5px rgba(255, 81, 47, 0.4)' }} />
        
        <Container maxWidth="md" sx={{ position: 'relative', zIndex: 1, textAlign: 'center' }}>
          <Security sx={{ fontSize: 50, color: '#FF6A00', mb: 2 }} />
          <Typography variant="h2" fontWeight="900" color="white" sx={{ mb: 2, letterSpacing: '-1px', fontSize: { xs: '2.5rem', md: '3.5rem' } }}>
            Privacy Policy
          </Typography>
          <Typography variant="h6" color="#94A3B8" fontWeight="500">
            Last updated: January 2025
          </Typography>
        </Container>
      </Box>

      {/* 🌟 MAIN CONTENT 🌟 */}
      <Container maxWidth="md" sx={{ mt: { xs: -4, md: -6 }, position: 'relative', zIndex: 2 }}>
        <Paper 
          elevation={0} 
          sx={{ 
            p: { xs: 3, md: 6 }, 
            borderRadius: 4, 
            border: '1px solid rgba(0,0,0,0.05)',
            boxShadow: '0 10px 40px rgba(0,0,0,0.03)'
          }}
        >
          
          <SectionHeader icon={<Description />} title="1. Introduction" />
          <Typography variant="body1" color="#475569" lineHeight={1.8}>
            <strong>PracharApp</strong> ("we," "our," or "us") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our website and mobile application (collectively, the "Service").
            <br/><br/>
            Please read this Privacy Policy carefully. If you do not agree with the terms of this Privacy Policy, please do not access the Service.
          </Typography>

          <SectionHeader icon={<DataUsage />} title="2. Information We Collect" />
          
          <Typography variant="subtitle1" fontWeight="700" color="#1E293B" mt={3} mb={1}>Personal Information</Typography>
          <Typography variant="body1" color="#475569" mb={1} lineHeight={1.8}>
            We may collect personal information that you voluntarily provide to us when you:
          </Typography>
          <Grid container spacing={2} mb={2}>
            <Grid item xs={12} sm={6}><CustomListItem text="Register for an account" /></Grid>
            <Grid item xs={12} sm={6}><CustomListItem text="Make a purchase" /></Grid>
            <Grid item xs={12} sm={6}><CustomListItem text="Contact us for support" /></Grid>
            <Grid item xs={12} sm={6}><CustomListItem text="Subscribe to our newsletter" /></Grid>
          </Grid>
          
          <Typography variant="body1" color="#475569" mb={1} lineHeight={1.8}>This information may include:</Typography>
          <List disablePadding sx={{ mb: 3 }}>
            <CustomListItem text="Name and contact information" />
            <CustomListItem text="Email address and phone number" />
            <CustomListItem text="Payment information" />
            <CustomListItem text="Profile information and preferences" />
          </List>

          <Typography variant="subtitle1" fontWeight="700" color="#1E293B" mb={1}>Usage Information</Typography>
          <Typography variant="body1" color="#475569" mb={1} lineHeight={1.8}>
            We automatically collect certain information when you use our Service:
          </Typography>
          <List disablePadding>
            <CustomListItem text="Device information (IP address, browser type, OS)" />
            <CustomListItem text="Usage patterns and preferences" />
            <CustomListItem text="Log files and analytics data" />
            <CustomListItem text="Cookies and similar tracking technologies" />
          </List>

          <SectionHeader icon={<SettingsSuggest />} title="3. How We Use Your Information" />
          <Typography variant="body1" color="#475569" mb={2} lineHeight={1.8}>
            We use the information we collect for various purposes, including:
          </Typography>
          <Grid container spacing={1}>
            <Grid item xs={12} sm={6}><CustomListItem text="Providing and maintaining our Service" /></Grid>
            <Grid item xs={12} sm={6}><CustomListItem text="Processing transactions and payments" /></Grid>
            <Grid item xs={12} sm={6}><CustomListItem text="Sending administrative information" /></Grid>
            <Grid item xs={12} sm={6}><CustomListItem text="Responding to customer service requests" /></Grid>
            <Grid item xs={12} sm={6}><CustomListItem text="Improving our Service and user experience" /></Grid>
            <Grid item xs={12} sm={6}><CustomListItem text="Sending marketing communications" /></Grid>
            <Grid item xs={12} sm={6}><CustomListItem text="Detecting and preventing fraud" /></Grid>
            <Grid item xs={12} sm={6}><CustomListItem text="Complying with legal obligations" /></Grid>
          </Grid>

          <SectionHeader icon={<Share />} title="4. Information Sharing and Disclosure" />
          <Typography variant="body1" color="#475569" mb={2} lineHeight={1.8}>
            We do not sell, trade, or otherwise transfer your personal information to third parties except in the following circumstances:
          </Typography>
          
          <Typography variant="subtitle2" fontWeight="700" color="#1E293B" mb={1}>Service Providers</Typography>
          <Typography variant="body2" color="#475569" mb={1} lineHeight={1.6}>
            We may share your information with trusted third-party service providers who assist us in operating our Service (e.g., Payment processors, Cloud storage, Analytics, Customer support).
          </Typography>

          <Typography variant="subtitle2" fontWeight="700" color="#1E293B" mt={2} mb={1}>Legal Requirements</Typography>
          <Typography variant="body2" color="#475569" lineHeight={1.6}>
            We may disclose your information if required by law or in response to valid legal requests.
          </Typography>

          <SectionHeader icon={<Shield />} title="5. Data Security" />
          <Typography variant="body1" color="#475569" mb={2} lineHeight={1.8}>
            We implement appropriate technical and organizational security measures to protect your personal information:
          </Typography>
          <List disablePadding sx={{ mb: 2 }}>
            <CustomListItem text="Encryption of data in transit and at rest" />
            <CustomListItem text="Regular security assessments" />
            <CustomListItem text="Access controls and authentication" />
            <CustomListItem text="Employee training on data protection" />
          </List>
          <Typography variant="body2" color="#64748B" fontStyle="italic">
            Note: No method of transmission over the Internet or electronic storage is 100% secure, and we cannot guarantee absolute security.
          </Typography>

          <SectionHeader icon={<Save />} title="6. Data Retention" />
          <Typography variant="body1" color="#475569" lineHeight={1.8}>
            We retain your personal information only for as long as necessary to fulfill the purposes outlined in this Privacy Policy, unless a longer retention period is required or permitted by law. When we no longer need your information, we will securely delete or anonymize it.
          </Typography>

          <SectionHeader icon={<ManageAccounts />} title="7. Your Rights and Choices" />
          <Typography variant="body1" color="#475569" mb={2} lineHeight={1.8}>
            Depending on your location, you may have certain rights regarding your personal information:
          </Typography>
          <List disablePadding sx={{ mb: 2 }}>
            <CustomListItem text="Access: Request access to your personal information" />
            <CustomListItem text="Correction: Request correction of inaccurate information" />
            <CustomListItem text="Deletion: Request deletion of your personal information" />
            <CustomListItem text="Portability: Request a copy of your data in a portable format" />
            <CustomListItem text="Opt-out: Unsubscribe from marketing communications" />
          </List>

          <SectionHeader icon={<Cookie />} title="8. Cookies and Tracking" />
          <Typography variant="body1" color="#475569" lineHeight={1.8}>
            We use cookies and similar tracking technologies to remember your preferences, analyze usage patterns, provide personalized content, and improve our Service. You can control cookies through your browser settings.
          </Typography>

          <SectionHeader icon={<ChildCare />} title="9. Children's Privacy" />
          <Typography variant="body1" color="#475569" lineHeight={1.8}>
            Our Service is not intended for children under the age of 13. We do not knowingly collect personal information from children under 13. If we become aware that we have collected such data, we will take steps to delete it.
          </Typography>

          <SectionHeader icon={<Public />} title="10. International Transfers" />
          <Typography variant="body1" color="#475569" lineHeight={1.8}>
            Your information may be transferred to and processed in countries other than your own. We ensure that such transfers are conducted in accordance with applicable data protection laws and safeguards.
          </Typography>

          <SectionHeader icon={<Update />} title="11. Changes to Policy" />
          <Typography variant="body1" color="#475569" lineHeight={1.8}>
            We may update this Privacy Policy from time to time. We will notify you of any material changes by posting the new Privacy Policy on this page and updating the "Last updated" date.
          </Typography>

          <Divider sx={{ my: 6, borderColor: 'rgba(0,0,0,0.08)' }} />

          {/* 🌟 CONTACT BOX 🌟 */}
          <Box sx={{ bgcolor: '#f1f5f9', p: 4, borderRadius: 3 }}>
            <Typography variant="h6" fontWeight="800" color="#1E293B" mb={2} display="flex" alignItems="center">
              <Email sx={{ mr: 1, color: '#FF6A00' }} /> 12. Contact Us
            </Typography>
            <Typography variant="body2" color="#64748B" mb={2}>
              If you have any questions about this Privacy Policy or our privacy practices, please contact us:
            </Typography>
            <Typography variant="subtitle1" color="#1E293B" fontWeight="700">
              support@pracharapp.com
            </Typography>
          </Box>

        </Paper>
      </Container>

      {/* 🌟 BOTTOM CTA SECTION 🌟 */}
      <Container maxWidth="md" sx={{ mt: 6 }}>
        <Box 
          sx={{ 
            background: primaryGradient, borderRadius: 4, p: { xs: 4, md: 5 }, 
            textAlign: 'center', color: 'white', boxShadow: '0 10px 20px rgba(238, 9, 121, 0.2)'
          }}
        >
          <ShieldOutlined sx={{ fontSize: 40, mb: 1, opacity: 0.9 }} />
          <Typography variant="h4" fontWeight="900" mb={1}>Questions About Your Privacy?</Typography>
          <Typography variant="subtitle1" mb={4} sx={{ opacity: 0.9 }}>
            We're committed to transparency and protecting your personal information.
          </Typography>
          <Button 
            component="a" href="mailto:privacy@pracharapp.com"
            variant="contained" startIcon={<Email />}
            sx={{ 
              bgcolor: 'white', color: '#EE0979', px: 4, py: 1.5, 
              borderRadius: '50px', fontWeight: '800', fontSize: '1.05rem',
              '&:hover': { bgcolor: '#f8fafc', transform: 'translateY(-2px)' },
              transition: 'all 0.3s'
            }}
          >
            Contact Privacy Team
          </Button>
        </Box>
      </Container>

    </Box>
  );
}